import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ResponsiveContainer, AreaChart, Area, YAxis } from 'recharts';
import { collapseStrategies, sideTagClass, sideTagText } from '../../../utils/optionStrategies';
import {
  ALL_ACCOUNTS,
  accountsInChoice,
  brokerLabel,
  cleanAccountName,
  getMyAccountBalances,
  getMyPositions,
  summarizeAccount,
} from './myDataApi';
import {
  usd, pct, tone, shortDate, niceDate, rangeLabel, UP, DOWN, BrokerMark,
} from './myWidgetUi';

/*
 * "Where is this coming from" and "tell me more" for the My accounts widgets:
 * a source line under each widget title, and a detail view per widget.
 */

/**
 * What a widget covers, kept short: "5 accounts" (or the one account's name).
 * Click it for the same detail view as the widget; hover lists the accounts.
 * `missing` flags accounts that didn't load this time.
 */
export function SourceLine({ choice = ALL_ACCOUNTS, extra = '', onClick, missing = [] }) {
  const accounts = accountsInChoice(choice);
  const names = accounts.map((a) => `${cleanAccountName(a.name)} (${brokerLabel(a.broker)})`).join('\n');
  const label = accounts.length === 1
    ? cleanAccountName(accounts[0].name)
    : `${accounts.length || ''} ${accounts.length === 1 ? 'account' : 'accounts'}`.trim();
  const body = (
    <>
      {label}
      {missing.length ? <b className="mydw-source-warn"> · {missing.length} didn’t load</b> : null}
      {extra ? <span className="mydw-source-extra"> · {extra}</span> : null}
    </>
  );
  const title = [names && `Pulling from:\n${names}`, missing.length && `Didn’t load: ${missing.map((a) => cleanAccountName(a.name)).join(', ')}`]
    .filter(Boolean).join('\n\n');
  if (!onClick) return <span className="mydw-source" title={title || undefined}>{body}</span>;
  return (
    <button type="button" className="mydw-source is-link" title={title || undefined} onClick={onClick}>
      {body}
    </button>
  );
}

/** Modal frame shared by every detail view. */
export function InsightModal({ title, subtitle, onClose, children, wide = false, headerAction = null }) {
  const ref = useRef(null);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    ref.current?.focus();
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return createPortal(
    // Stop clicks here reaching the widget behind (React bubbles events out of portals).
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div className="mydw-day-backdrop" onClick={(e) => e.stopPropagation()} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={`mydw-day mydw-insight${wide ? ' is-wide' : ''}`} role="dialog" aria-modal="true" aria-label={title} tabIndex={-1} ref={ref}>
        <header className="mydw-insight-head">
          <div>
            <strong>{title}</strong>
            {subtitle ? <span>{subtitle}</span> : null}
          </div>
          {headerAction ? <div className="mydw-insight-action">{headerAction}</div> : null}
          <button type="button" className="mydw-insight-close" onClick={onClose} aria-label="Close">×</button>
        </header>
        {children}
      </div>
    </div>,
    document.body,
  );
}

function MiniSpark({ points, up }) {
  if (!points || points.length < 2) return <span className="mydw-ins-spark" />;
  const data = points.map((p) => ({ v: Number(p.value) || 0 }));
  return (
    <span className="mydw-ins-spark">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 2, right: 0, bottom: 2, left: 0 }}>
          <YAxis hide domain={['auto', 'auto']} />
          <Area type="monotone" dataKey="v" stroke={up ? UP : DOWN} fill={up ? UP : DOWN} fillOpacity={0.15} strokeWidth={1.5} isAnimationActive={false} />
        </AreaChart>
      </ResponsiveContainer>
    </span>
  );
}

const METRIC_COLUMN = {
  equity: 'value', netPnl: 'change', todayPnl: 'day', maxDrawdown: 'dd', winDayRate: 'win', profitFactor: 'pf',
  bestDay: 'best', worstDay: 'worst', tradingDays: 'win',
};

/**
 * Every account's numbers for the widget's period: value, change, return,
 * last day, drawdown, winning days, profit factor. `highlight` marks the
 * column the clicked KPI is about.
 */
export function AccountBreakdown({ perf, range, highlight }) {
  const rows = useMemo(() => (perf?.perAccount || []).map((a) => {
    const s = summarizeAccount(a.points);
    return { ...a, s };
  }).sort((a, b) => b.s.equity - a.s.equity), [perf]);
  const total = perf?.summary;
  const col = METRIC_COLUMN[highlight] || null;
  const cls = (key) => (col === key ? 'is-hl' : '');
  if (!rows.length) return <p className="mydw-day-empty">No account history for this period yet.</p>;
  return (
    <div className="mydw-ins-table-wrap">
      <table className="mydw-ins-table">
        <thead>
          <tr>
            <th>Account</th>
            <th className={`num ${cls('value')}`}>Value</th>
            <th className={`num ${cls('change')}`}>Change</th>
            <th className="num">Return</th>
            <th className={`num ${cls('day')}`}>Last day</th>
            <th className={`num ${cls('dd')}`}>Max drawdown</th>
            <th className={`num ${cls('win')}`}>Winning days</th>
            <th className={`num ${cls('pf')}`}>Profit factor</th>
            <th>{rangeLabel(range)}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ id, name, broker, points, s }) => (
            <tr key={id}>
              <td>
                <span className="mydw-ins-acct">
                  <BrokerMark broker={broker} name={name} size={20} />
                  <span><strong>{cleanAccountName(name)}</strong><small>{brokerLabel(broker)}</small></span>
                </span>
              </td>
              <td className={`num ${cls('value')}`}>{usd(s.equity)}</td>
              <td className={`num ${tone(s.netPnl)} ${cls('change')}`}>{usd(s.netPnl, { sign: true })}</td>
              <td className={`num ${tone(s.returnPct)}`}>{pct(s.returnPct)}</td>
              <td className={`num ${tone(s.lastDayPnl)} ${cls('day')}`}>{usd(s.lastDayPnl, { sign: true, cents: true })}</td>
              <td className={`num is-down ${cls('dd')}`}>{s.maxDrawdown ? usd(s.maxDrawdown) : '—'}</td>
              <td className={`num ${cls('win')}`}>{s.tradingDays ? `${Math.round(s.winDayRate * 100)}% · ${s.winDays}/${s.tradingDays}` : '—'}</td>
              <td className={`num ${cls('pf')}`}>{Number.isFinite(s.profitFactor) ? s.profitFactor.toFixed(2) : '∞'}</td>
              <td><MiniSpark points={points} up={s.netPnl >= 0} /></td>
            </tr>
          ))}
        </tbody>
        {total && rows.length > 1 ? (
          <tfoot>
            <tr>
              <td><strong>All accounts</strong></td>
              <td className={`num ${cls('value')}`}>{usd(total.equity)}</td>
              <td className={`num ${tone(total.netPnl)} ${cls('change')}`}>{usd(total.netPnl, { sign: true })}</td>
              <td className={`num ${tone(total.returnPct)}`}>{pct(total.returnPct)}</td>
              <td className={`num ${tone(total.lastDayPnl)} ${cls('day')}`}>{usd(total.lastDayPnl, { sign: true, cents: true })}</td>
              <td className={`num is-down ${cls('dd')}`}>{total.maxDrawdown ? usd(total.maxDrawdown) : '—'}</td>
              <td className={`num ${cls('win')}`}>{total.tradingDays ? `${Math.round(total.winDayRate * 100)}% · ${total.winDays}/${total.tradingDays}` : '—'}</td>
              <td className={`num ${cls('pf')}`}>{Number.isFinite(total.profitFactor) ? total.profitFactor.toFixed(2) : '∞'}</td>
              <td />
            </tr>
          </tfoot>
        ) : null}
      </table>
    </div>
  );
}

/** What you hold across every account, by symbol, plus cash. */
export function HoldingsInsight() {
  const [state, setState] = useState({ loading: true });
  useEffect(() => {
    let live = true;
    Promise.all([getMyPositions(ALL_ACCOUNTS), getMyAccountBalances()])
      .then(([pos, balances]) => { if (live) setState({ loading: false, pos, balances }); })
      .catch((e) => { if (live) setState({ loading: false, error: e.message }); });
    return () => { live = false; };
  }, []);
  const view = useMemo(() => {
    if (!state.pos) return null;
    const bySymbol = new Map();
    state.pos.positions.forEach((p) => {
      const key = p.symbol;
      const row = bySymbol.get(key) || { symbol: key, value: 0, upl: 0, accounts: [] };
      row.value += Number(p.marketValue) || 0;
      row.upl += Number(p.unrealizedPl ?? p.unrealizedPnl ?? 0) || 0;
      row.accounts.push({ name: p.account, broker: p.broker });
      bySymbol.set(key, row);
    });
    const held = [...bySymbol.values()].sort((a, b) => b.value - a.value);
    const heldTotal = held.reduce((s, r) => s + r.value, 0);
    const total = (state.balances || []).reduce((s, b) => s + Math.max(0, Number(b.equity) || 0), 0);
    const cash = Math.max(0, total - heldTotal);
    return { held, heldTotal, total, cash };
  }, [state]);
  if (state.loading) return <p className="mydw-day-empty">Loading your holdings…</p>;
  if (state.error) return <p className="mydw-day-empty">{state.error}</p>;
  const { held, total, cash } = view;
  return (
    <>
      <div className="mydw-day-stats">
        <div><span>Total</span><b>{usd(total)}</b></div>
        <div><span>Invested</span><b>{usd(view.heldTotal)}</b></div>
        <div><span>Cash &amp; other</span><b>{usd(cash)}</b></div>
        <div><span>Symbols</span><b>{held.length}</b></div>
      </div>
      <section className="mydw-day-sec">
        <h4>What you hold, across every account</h4>
        {!held.length ? <p className="mydw-day-empty">No open positions reported.</p> : (
          <ul className="mydw-day-accts">
            {held.map((r) => (
              <li key={r.symbol}>
                <span className="mydw-ins-sym">{r.symbol.slice(0, 4)}</span>
                <span className="mydw-day-acct-name">
                  <strong>{r.symbol}</strong>
                  <small className="mydw-ins-held-in">
                    {r.accounts.map((a, i) => <BrokerMark key={`${a.name}-${i}`} broker={a.broker} name={a.name} size={12} />)}
                    {r.accounts.map((a) => cleanAccountName(a.name)).join(', ')}
                  </small>
                  <span className="mydw-day-acct-bar"><i style={{ width: `${total ? (r.value / total) * 100 : 0}%`, color: 'var(--bt-818cf8, #818cf8)' }} /></span>
                </span>
                <span className="mydw-alloc-num">
                  <b>{usd(r.value)}</b>
                  <small className={tone(r.upl)}>{usd(r.upl, { sign: true })} · {total ? ((r.value / total) * 100).toFixed(1) : 0}%</small>
                </span>
              </li>
            ))}
          </ul>
        )}
        {state.pos.unsupported?.length ? <p className="mydw-foot">Per-symbol holdings aren’t available for {state.pos.unsupported.map(cleanAccountName).join(', ')}; their value counts as cash &amp; other.</p> : null}
      </section>
    </>
  );
}

/** Every trade, with account / outcome filters and search. */
export function TradesInsight({ trades: rawTrades }) {
  const trades = useMemo(() => collapseStrategies(rawTrades), [rawTrades]);
  const [acct, setAcct] = useState('all');
  const [outcome, setOutcome] = useState('all');
  const [q, setQ] = useState('');
  const accounts = useMemo(() => [...new Set(trades.map((t) => t.account))], [trades]);
  const shown = useMemo(() => trades.filter((t) => {
    if (acct !== 'all' && t.account !== acct) return false;
    const p = Number(t.profit) || 0;
    if (outcome === 'win' && p <= 0) return false;
    if (outcome === 'loss' && p >= 0) return false;
    if (q && !String(t.symbol || '').toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  }), [trades, acct, outcome, q]);
  const pnl = shown.map((t) => Number(t.profit) || 0);
  const wins = pnl.filter((p) => p > 0);
  const losses = pnl.filter((p) => p < 0);
  const sum = (a) => a.reduce((s, v) => s + v, 0);
  return (
    <>
      <div className="mydw-ins-filters">
        <select value={acct} onChange={(e) => setAcct(e.target.value)} aria-label="Account">
          <option value="all">All accounts</option>
          {accounts.map((a) => <option key={a} value={a}>{cleanAccountName(a)}</option>)}
        </select>
        <div className="mydw-pills" role="tablist" aria-label="Outcome">
          {[['all', 'All'], ['win', 'Winners'], ['loss', 'Losers']].map(([v, l]) => (
            <button key={v} type="button" role="tab" aria-selected={outcome === v} className={outcome === v ? 'active' : ''} onClick={() => setOutcome(v)}>{l}</button>
          ))}
        </div>
        <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Symbol" aria-label="Search symbol" />
      </div>
      <div className="mydw-day-stats">
        <div><span>Realized</span><b className={tone(sum(pnl))}>{usd(sum(pnl), { sign: true })}</b></div>
        <div><span>Trades</span><b>{shown.length}{shown.length ? ` · ${Math.round((wins.length / shown.length) * 100)}% won` : ''}</b></div>
        <div><span>Avg win / loss</span><b>{wins.length ? usd(sum(wins) / wins.length) : '—'} / {losses.length ? usd(sum(losses) / losses.length) : '—'}</b></div>
        <div><span>Best / worst</span><b>{pnl.length ? `${usd(Math.max(...pnl), { sign: true })} / ${usd(Math.min(...pnl), { sign: true })}` : '—'}</b></div>
      </div>
      <ul className="mydw-trade-list mydw-ins-list">
        {shown.slice(0, 300).map((t, i) => {
          const p = Number(t.profit) || 0;
          return (
            <li key={`${t.symbol}-${t.exitDate}-${i}`}>
              <BrokerMark broker={t.broker} name={t.account} size={22} />
              <span className="mydw-trade-main">
                <strong>{t.symbol}{t.quantity != null ? <small className="mydw-qty"> × {Number(t.quantity).toLocaleString('en-US', { maximumFractionDigits: 4 })}</small> : null}</strong>
                <small>
                  {cleanAccountName(t.account)} · {niceDate(String(t.exitDate || t.entryDate || '').slice(0, 10))}
                  {t.legsText ? ` · ${t.legsText}` : ''}
                  {t.entryPrice != null && t.exitPrice != null ? ` · ${usd(t.entryPrice, { cents: true })} → ${usd(t.exitPrice, { cents: true })}` : ''}
                  {t.entryDate && t.entryDate !== t.exitDate ? ` · opened ${shortDate(String(t.entryDate).slice(0, 10))}` : ''}
                </small>
              </span>
              {sideTagText(t) ? <span className={`mydw-side ${sideTagClass(t)}`}>{sideTagText(t)}</span> : null}
              <b className={`mydw-day-trade-pnl ${tone(p)}`}>{usd(p, { sign: true, cents: true })}</b>
            </li>
          );
        })}
        {!shown.length ? <li className="mydw-ins-none">No trades match.</li> : null}
      </ul>
      {shown.length > 300 ? <p className="mydw-day-hint">Showing the latest 300 of {shown.length}.</p> : null}
    </>
  );
}

/** Every open position, filterable by account, sortable. */
export function PositionsInsight({ positions: rawPositions }) {
  const positions = useMemo(() => collapseStrategies(rawPositions), [rawPositions]);
  const [acct, setAcct] = useState('all');
  const [sort, setSort] = useState('value');
  const accounts = useMemo(() => [...new Set(positions.map((p) => p.account))], [positions]);
  const shown = useMemo(() => positions
    .filter((p) => acct === 'all' || p.account === acct)
    .slice()
    .sort((a, b) => (sort === 'upl'
      ? (Number(b.unrealizedPl) || 0) - (Number(a.unrealizedPl) || 0)
      : (Number(b.marketValue) || 0) - (Number(a.marketValue) || 0))), [positions, acct, sort]);
  const value = shown.reduce((s, p) => s + (Number(p.marketValue) || 0), 0);
  const upl = shown.reduce((s, p) => s + (Number(p.unrealizedPl) || 0), 0);
  const basis = value - upl;
  return (
    <>
      <div className="mydw-ins-filters">
        <select value={acct} onChange={(e) => setAcct(e.target.value)} aria-label="Account">
          <option value="all">All accounts</option>
          {accounts.map((a) => <option key={a} value={a}>{cleanAccountName(a)}</option>)}
        </select>
        <div className="mydw-pills" role="tablist" aria-label="Sort">
          {[['value', 'Largest'], ['upl', 'Best performing']].map(([v, l]) => (
            <button key={v} type="button" role="tab" aria-selected={sort === v} className={sort === v ? 'active' : ''} onClick={() => setSort(v)}>{l}</button>
          ))}
        </div>
      </div>
      <div className="mydw-day-stats">
        <div><span>Market value</span><b>{usd(value)}</b></div>
        <div><span>Cost</span><b>{usd(basis)}</b></div>
        <div><span>Unrealized</span><b className={tone(upl)}>{usd(upl, { sign: true })}{basis > 0 ? ` (${pct(upl / basis)})` : ''}</b></div>
      </div>
      <ul className="mydw-trade-list mydw-ins-list">
        {shown.map((p, i) => {
          const u = Number(p.unrealizedPl);
          return (
            <li key={`${p.symbol}-${i}`}>
              <BrokerMark broker={p.broker} name={p.account} size={22} />
              <span className="mydw-trade-main">
                <strong>{p.symbol}</strong>
                <small>
                  {cleanAccountName(p.account)} · {p.isStrategy ? `${p.sideLabel} · ${p.legsText}` : `${p.quantity ?? '—'} @ ${p.currentPrice != null ? usd(p.currentPrice, { cents: true }) : '—'}`}
                  {p.avgEntryPrice != null ? ` · cost ${usd(p.avgEntryPrice, { cents: true })}` : ''}
                </small>
              </span>
              <span className="mydw-pos-val">
                <b>{p.marketValue != null ? usd(p.marketValue) : '—'}</b>
                <small className={tone(u)}>{Number.isFinite(u) ? usd(u, { sign: true }) : ''}{p.unrealizedPlpc != null ? ` (${pct(Number(p.unrealizedPlpc))})` : ''}</small>
              </span>
            </li>
          );
        })}
      </ul>
    </>
  );
}
